import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Tabs,
  Tab,
  Link,
  Divider,
  Alert,
} from '@mui/material';
import {
  ExpandMore,
  Send,
  HelpOutline,
  Article,
  Email,
  Search,
  CheckCircle,
} from '@mui/icons-material';
import { useSnackbar } from 'src/view/components/SnackbarService';
import clientService from 'src/clientService';
import { SupportTicketCreate, TicketCategory, TicketPriority } from 'src/api/Api';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQ_DATA: FAQItem[] = [
  {
    category: 'General',
    question: 'What is Darukaa.Earth?',
    answer:
      'Darukaa.Earth is a cloud-native biodiversity and climate-risk platform for monitoring, analysis, carbon & biodiversity credit calculation, and stakeholder workflows. It combines remote sensing, in-situ sensors (acoustic & camera), AI/ML models, and reporting tools.',
  },
  {
    category: 'General',
    question: 'Who should use the platform?',
    answer:
      'Project developers, conservation/NGO teams, verifiers, researchers, and enterprises doing nature-based projects: carbon, biodiversity, restoration, or climate risk assessment.',
  },
  {
    category: 'Accounts & Authentication',
    question: 'How do I create an account?',
    answer:
      'Sign up via the web UI or use the API endpoint POST /api/v1/auth/signup. Verify your email to complete registration.',
  },
  {
    category: 'Accounts & Authentication',
    question: 'How do I authenticate with the API?',
    answer:
      'Obtain an access token by calling POST /auth/login with email/password. Send Authorization: Bearer <access_token> in API requests.',
  },
  {
    category: 'Accounts & Authentication',
    question: 'How long are tokens valid and how do I refresh them?',
    answer:
      'Access tokens are short-lived; use POST /auth/refresh with your refresh token to get a new access token. Token lifetimes are configured on the server.',
  },
  {
    category: 'Data & Uploads',
    question: 'What formats are supported for geodata and media?',
    answer:
      'Vectors: GeoJSON (RFC 7946), shapefile zip. Rasters: GeoTIFF. Audio: WAV (16-bit, ≥44.1 kHz preferred), MP3, M4A, WEBM. Images: JPEG/PNG with EXIF.',
  },
  {
    category: 'Data & Uploads',
    question: 'What are file size and upload limits?',
    answer:
      'Single upload max: 500 MB for general uploads. Audio upload: 25 MB per file, max 20 files per request, 300 seconds max duration.',
  },
  {
    category: 'Troubleshooting',
    question: 'I received 429 Too Many Requests — what now?',
    answer:
      'Back off and retry after Retry-After header. Implement exponential backoff and respect rate-limit headers (X-RateLimit-Limit, X-RateLimit-Remaining, X-RateLimit-Reset).',
  },
  {
    category: 'Troubleshooting',
    question: 'My file upload returns 413 Payload Too Large — what should I do?',
    answer:
      'Split the upload into smaller chunks if supported, compress/convert files, or use the platform\'s batch upload API with signed URLs. Contact support if you need higher limits.',
  },
  {
    category: 'Troubleshooting',
    question: 'I can\'t find a project or site — 404 — what to check?',
    answer:
      'Verify you\'re using the correct project_id/site_id, that the account has access, and that the get_satellite_images flag (if used) is correct. Provide request_id and a timestamp when contacting support.',
  },
];

const HelpSupportPage: React.FC = () => {
  const { showSnackbar, snackbarType } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketSubmitted, setTicketSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    category: 'General',
    priority: 'Medium',
  });
  const [errors, setErrors] = useState({
    subject: '',
    message: '',
  });

  const categories = ['General', 'Technical', 'Billing', 'Feature Request', 'Bug Report', 'Account'];
  const priorities = ['Low', 'Medium', 'High', 'Urgent'];

  const filteredFAQs = FAQ_DATA.filter((faq) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      faq.question.toLowerCase().includes(query) ||
      faq.answer.toLowerCase().includes(query) ||
      faq.category.toLowerCase().includes(query)
    );
  });

  const groupedFAQs = filteredFAQs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {} as Record<string, FAQItem[]>);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;
    const newErrors = { subject: '', message: '' };

    if (!formData.subject.trim() || formData.subject.trim().length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters.';
      isValid = false;
    }

    if (!formData.message.trim() || formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showSnackbar({
        message: 'Please fix the errors in the form.',
        type: snackbarType.ERROR,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const requestBody: SupportTicketCreate = {
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        category: formData.category as TicketCategory,
        priority: formData.priority as TicketPriority,
      };
      
      console.log('Submitting support ticket with data:', requestBody);
      
      await clientService.api.createSupportTicketApiV1SupportTicketsPost(requestBody);

      // Success case - if we reach here, the request was successful
      setTicketSubmitted(true);
      setFormData({
        subject: '',
        message: '',
        category: 'General',
        priority: 'Medium',
      });
      showSnackbar({
        message: 'Support ticket submitted successfully! We\'ll get back to you soon.',
        type: snackbarType.SUCCESS,
      });
    } catch (error: any) {
      console.error('Error submitting support ticket:', error);
      console.error('Error type:', typeof error);
      console.error('Error keys:', Object.keys(error || {}));
      console.error('Error.error:', error?.error);
      console.error('Error.status:', error?.status);
      
      // The API client throws the HttpResponse when !response.ok
      // Try to read error body from various possible locations
      let errorData = null;
      
      // Check if error is the HttpResponse object itself - error field contains parsed error body
      if (error?.error !== null && error?.error !== undefined) {
        errorData = error.error;
        console.log('Found error in error.error field');
      } 
      // Check data field as fallback
      else if (error?.data !== null && error?.data !== undefined) {
        errorData = error.data;
        console.log('Found error in error.data field');
      } 
      // Try to read the response body if it's a Response-like object
      else {
        try {
          // Check if error has a json() method (Response object)
          if (error && typeof error.json === 'function') {
            errorData = await error.json();
            console.log('Read error from error.json()');
          } 
          // Check if error has a text() method
          else if (error && typeof error.text === 'function') {
            const text = await error.text();
            try {
              errorData = JSON.parse(text);
            } catch {
              errorData = { detail: text };
            }
            console.log('Read error from error.text()');
          }
          // Check if error has a response property (nested response)
          else if (error?.response && typeof error.response.json === 'function') {
            errorData = await error.response.json();
            console.log('Read error from error.response.json()');
          }
        } catch (e) {
          console.error('Failed to read error response body:', e);
        }
      }
      
      console.error('Extracted error data:', errorData);
      console.error('Error data detail:', errorData?.detail);
      if (Array.isArray(errorData?.detail)) {
        console.error('Validation errors:', errorData.detail.map((e: any) => ({
          loc: e.loc,
          msg: e.msg,
          type: e.type
        })));
      }
      
      // Extract error message from FastAPI validation error format
      let errorMessage = 'Failed to submit support ticket. Please try again.';
      
      // Check various error formats
      const errorDetail = errorData?.detail || error?.detail;
      
      if (errorDetail) {
        // Handle FastAPI validation errors (422) - array of validation errors
        if (Array.isArray(errorDetail)) {
          const validationErrors = errorDetail.map((err: any) => {
            // Extract field name from location array (usually last element)
            const fieldPath = err.loc || [];
            const field = fieldPath[fieldPath.length - 1] || 'field';
            const msg = err.msg || 'Validation error';
            return `${field}: ${msg}`;
          });
          errorMessage = validationErrors.join(', ');
        } else if (typeof errorDetail === 'string') {
          errorMessage = errorDetail;
        } else {
          errorMessage = JSON.stringify(errorDetail);
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      console.error('Final error message:', errorMessage);
      
      showSnackbar({
        message: errorMessage,
        type: snackbarType.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          Help & Support
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Find answers to common questions or contact our support team
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, newValue) => {
          setActiveTab(newValue);
          setTicketSubmitted(false);
        }}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            color: 'text.secondary',
            '&:hover': {
              color: '#ffb400',
            },
            '&.Mui-selected': {
              color: '#45987A',
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#45987A',
          },
        }}
      >
        <Tab label="FAQs" icon={<HelpOutline />} iconPosition="start" />
        <Tab label="Contact Support" icon={<Send />} iconPosition="start" />
        <Tab label="Documentation" icon={<Article />} iconPosition="start" />
      </Tabs>

      {/* FAQs Tab */}
      {activeTab === 0 && (
        <Box>
          <Paper sx={{ p: 3, mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#ffb400',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#45987A',
                  },
                },
              }}
            />
            {searchQuery && (
              <Typography variant="body2" sx={{ mb: 2, color: '#45987A', fontWeight: 500 }}>
                Found {filteredFAQs.length} result{filteredFAQs.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Paper>

          {Object.keys(groupedFAQs).length === 0 ? (
            <Paper sx={{ p: 3 }}>
              <Typography variant="body1" color="text.secondary" align="center">
                No FAQs found matching your search.
              </Typography>
            </Paper>
          ) : (
            Object.entries(groupedFAQs).map(([category, faqs]) => (
              <Box key={category} sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#45987A' }}>
                  {category}
                </Typography>
                {faqs.map((faq, index) => (
                  <Accordion
                    key={index}
                    sx={{
                      mb: 1,
                      '&:before': {
                        display: 'none',
                      },
                      '&.Mui-expanded': {
                        border: '1px solid #45987A',
                        borderRadius: '4px',
                      },
                      '&:hover': {
                        border: '1px solid #ffb400',
                        borderRadius: '4px',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: '#45987A' }} />}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(255, 180, 0, 0.05)',
                        },
                        '& .MuiAccordionSummary-content': {
                          '&:hover': {
                            color: '#ffb400',
                          },
                        },
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#45987A' }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" color="text.secondary">
                        {faq.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            ))
          )}
        </Box>
      )}

      {/* Contact Support Tab */}
      {activeTab === 1 && (
        <Paper sx={{ p: 4 }}>
          {ticketSubmitted ? (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Ticket Submitted Successfully!
              </Typography>
              <Typography variant="body2">
                We've received your support request and will get back to you soon. You'll receive
                an email confirmation shortly.
              </Typography>
            </Alert>
          ) : (
            <>
              <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
                Submit a Support Ticket
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    error={!!errors.subject}
                    helperText={errors.subject || 'Brief description of your issue'}
                    disabled={isSubmitting}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#ffb400',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#45987A',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#45987A',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    SelectProps={{ native: true }}
                    disabled={isSubmitting}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#ffb400',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#45987A',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#45987A',
                      },
                    }}
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Priority"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    SelectProps={{ native: true }}
                    disabled={isSubmitting}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#ffb400',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#45987A',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#45987A',
                      },
                    }}
                  >
                    {priorities.map((pri) => (
                      <option key={pri} value={pri}>
                        {pri}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    error={!!errors.message}
                    helperText={errors.message || 'Please provide detailed information about your issue'}
                    disabled={isSubmitting}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#ffb400',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#45987A',
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#45987A',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
                      sx={{
                        backgroundColor: '#45987A',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#3a7a63',
                          boxShadow: '0 4px 8px rgba(69, 152, 122, 0.3)',
                        },
                        '&:active': {
                          backgroundColor: '#2d5f4d',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#cccccc',
                        },
                      }}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      )}

      {/* Documentation Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                <Article sx={{ mr: 1, verticalAlign: 'middle' }} />
                Documentation
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" paragraph>
                Access comprehensive documentation, guides, and API references.
              </Typography>
              <Button
                variant="outlined"
                href="https://docs.darukaa.com"
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={{
                  mt: 2,
                  borderColor: '#45987A',
                  color: '#45987A',
                  '&:hover': {
                    borderColor: '#ffb400',
                    backgroundColor: 'rgba(255, 180, 0, 0.05)',
                    color: '#ffb400',
                  },
                }}
              >
                View Documentation
              </Button>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                <Email sx={{ mr: 1, verticalAlign: 'middle' }} />
                Contact Information
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Email:</strong>{' '}
                  <Link
                    href="mailto:info@darukaa.com"
                    sx={{
                      color: '#45987A',
                      textDecoration: 'none',
                      '&:hover': {
                        color: '#ffb400',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    info@darukaa.com
                  </Link>
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Website:</strong>{' '}
                  <Link
                    href="https://www.darukaa.earth"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#45987A',
                      textDecoration: 'none',
                      '&:hover': {
                        color: '#ffb400',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    www.darukaa.earth
                  </Link>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>API Base URL:</strong>{' '}
                  <Link
                    href="https://api.darukaa.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: '#45987A',
                      textDecoration: 'none',
                      '&:hover': {
                        color: '#ffb400',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    api.darukaa.com
                  </Link>
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default HelpSupportPage;

